import { createTheme } from '@mui/material/styles';

// Shared component overrides used by every theme so cards/buttons stay
// consistent regardless of palette.
const sharedComponents = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        textTransform: 'none',
        fontWeight: 500,
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
      },
    },
  },
};

const sharedTypography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontWeight: 600 },
  h2: { fontWeight: 600 },
  h3: { fontWeight: 600 },
  h4: { fontWeight: 500 },
  h5: { fontWeight: 500 },
  h6: { fontWeight: 500 },
};

// ── Default (original) green theme ──────────────────────────────────────────
const defaultTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#81C784',
      light: '#A5D6A7',
      dark: '#66BB6A',
      contrastText: '#1B5E20',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
  },
  typography: sharedTypography,
  components: sharedComponents,
});

// ── Black theme with bluish-purple (blurple) accent ─────────────────────────
const blackTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#5468FF',       // blurple (blue-violet) primary
      light: '#7C8BFF',
      dark: '#3B4FE0',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#6C7BFF',       // slightly lighter blurple for accents
      light: '#8E9AFF',
      dark: '#4B5BD9',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#000000',    // pure black
      paper: '#0E0E14',      // very dark raised surface
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#A1A1AA',
    },
    divider: 'rgba(255,255,255,0.08)',
  },
  typography: sharedTypography,
  components: {
    ...sharedComponents,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(90deg, #0E0E14 0%, #000000 100%)',
        },
      },
    },
  },
});

export const themes = {
  default: { id: 'default', label: 'Default (Green)', theme: defaultTheme },
  black:   { id: 'black',   label: 'Black (Red & Purple)', theme: blackTheme },
};

export const THEME_STORAGE_KEY = 'app.themeId';

export const getStoredThemeId = () => {
  try {
    const id = localStorage.getItem(THEME_STORAGE_KEY);
    return themes[id] ? id : 'default';
  } catch (_) {
    return 'default';
  }
};

// Back-compat: existing imports of { theme } keep working with the default.
export const theme = defaultTheme;
